import { useEffect, useRef } from "react";
import "./text-slide.css";
import { SLIDE_FALLBACK, BRAND, type TextSlideData } from "./text-slide.ts";

// The ONE renderer for text slides. Used by the player, the editor preview and
// the content cards, so a volunteer approves exactly what the TV will show.
//
// Before this there were three: `.slide-text` in player.css, `.slide-preview`
// and `.cp-text` in admin.css. They had drifted — the player set Georgia at 7vw
// with a sans subtext, the editor preview set no font-family at all and a fixed
// 2rem — so the preview was never quite the slide.
//
// Sizing: every dimension in text-slide.css is in `em`, relative to a root
// font-size of `--slide-u`, where one unit is 1% of the slide's width. The
// player sets that from the viewport; a boxed preview sets it from its own
// width. Same stylesheet, same proportions, any size.

export type SlideFit =
  /** Fills the viewport — the TV. */
  | "fill"
  /** Sized by its container — previews and cards. */
  | "box";

export default function TextSlideView({
  slide,
  fit,
  className = "",
  onFits,
}: {
  slide: TextSlideData;
  fit: SlideFit;
  className?: string;
  /** Reports whether the content fits the slide. The editor uses this to warn
   *  before saving: the slide clips what overflows, and silently publishing
   *  half a sentence to a wall is the failure worth catching here. Omitted by
   *  the player, which has nobody to tell. */
  onFits?: (fits: boolean) => void;
}) {
  const layout = slide.layout ?? "centered";
  const eyebrow = slide.eyebrow?.trim();
  const headline = slide.h?.trim();
  const meta = slide.m?.trim();
  const sub = slide.s?.trim();
  // Only the event layout reserves room for it; anywhere else it would land on
  // top of centred text.
  const qr =
    layout === "event" || layout === "sermon" ? slide.qr?.trim() : undefined;
  const qrCaption = slide.qrCaption?.trim();
  const bgImage = slide.bgImage?.trim();
  const corner = layout === "sermon" ? slide.corner?.trim() : undefined;

  const slideRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!onFits) return;
    const el = slideRef.current;
    if (!el) return;
    // 1px of slack: sub-pixel rounding on a scaled box otherwise reports a
    // permanent overflow on slides that plainly fit.
    const check = () => onFits(el.scrollHeight <= el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
    // Re-measure whenever the text or the layout changes.
  }, [onFits, layout, eyebrow, headline, meta, sub, qr, bgImage]);

  return (
    <div className={`ts-fit ts-fit--${fit} ${className}`.trim()}>
      <div
        ref={slideRef}
        className={`ts ts--${layout}`}
        style={{
          background: slide.bg ?? SLIDE_FALLBACK.bg,
          color: slide.fg ?? SLIDE_FALLBACK.fg,
          // The dim is a token rather than a second element so the picture and
          // its scrim stay one paint — a separate overlay div would sit above
          // the text in the stacking order unless everything else got a
          // z-index it does not otherwise need.
          ["--ts-bg-dim" as string]: String(slide.bgDim ?? 0),
          // Consumed by the rules, eyebrow and detail line.
          ["--ts-accent" as string]: slide.accent ?? BRAND.gold,
        }}
      >
        {bgImage && (
          <div
            className="ts-bg"
            style={{ backgroundImage: `url(${JSON.stringify(bgImage)})` }}
            aria-hidden="true"
          />
        )}
        {corner && <div className="ts-corner">{corner}</div>}
        {eyebrow && <div className="ts-eyebrow">{eyebrow}</div>}
        {headline && <div className="ts-h">{headline}</div>}
        {/* The rule is layout-driven, not content-driven: `centered` has never
            had one and adding it would restyle every existing slide. */}
        {(layout === "event" || layout === "welcome") && <div className="ts-rule" />}
        {meta && <div className="ts-m">{meta}</div>}
        {sub && <div className="ts-s">{sub}</div>}
        {qr && (
          <div className="ts-qr">
            {/* Empty alt: the caption beside it already says what it is, and
                the code's destination is not something a screen reader can
                usefully read out. */}
            <img className="ts-qr-img" src={qr} alt="" />
            {qrCaption && <div className="ts-qr-cap">{qrCaption}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
